import React, { useEffect, useState, useCallback } from 'react';
// @ts-expect-error - lodash.debounce doesn't have type definitions
import debounce from 'lodash.debounce';

import { Box, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { getActiveDocuments } from '@/actions/document';
import { useApi } from '@/providers/ApiContext';
import type { Document, EntityId } from '@/types';

interface ManifestReferenceDocumentFieldProps {
  document_type_id: EntityId;
  value: Document | null;
  setValue: (value: Document | null) => void;
  setChanged: () => void;
  edit?: boolean;
}

function ManifestReferenceDocumentField({
  document_type_id,
  value,
  setValue,
  setChanged,
  edit,
}: ManifestReferenceDocumentFieldProps): React.JSX.Element {
  const api = useApi();
  const [query, setQuery] = useState<string>('');
  const [documents, setDocuments] = useState<Document[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selected, setSelected] = useState<Document | null>(value);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const fetchDocuments = useCallback(
    (newQuery: string, startKey: string | null = null): void => {
      if (!api) return;
      debounce(async () => {
        setLoading(true);
        try {
          const response = await getActiveDocuments(
            api,
            document_type_id,
            20,
            startKey || undefined,
            Boolean(newQuery) ? newQuery : undefined,
          );
          setDocuments((prevOptions) =>
            startKey === null
              ? response.results
              : [...(prevOptions || []), ...response.results],
          );
          setHasNextPage(response.last_key !== null);
          if (response.last_key) {
            setCursors(
              (prev) =>
                ({
                  ...prev,
                  [Object.keys(prev).length + 1]: response.last_key,
                }) as Record<number, string>,
            );
          }
        } catch (error) {
          console.log(error);
          setDocuments([]);
        } finally {
          setLoading(false);
        }
      }, 300)();
    },
    [api, document_type_id],
  );

  useEffect(() => {
    if (isOpen && !documents) {
      setCursors({});
      fetchDocuments(''); // Fetch initial items when dropdown opens
    }
  }, [isOpen, documents, fetchDocuments]);

  useEffect(() => {
    if (query.length >= 1) {
      setCursors({});
      fetchDocuments(query); // Fetch new items for new query
    } else if (query.length === 0 && isOpen) {
      setCursors({});
      fetchDocuments(''); // Reset to initial items when query is cleared
    }
  }, [query, fetchDocuments, isOpen]);

  const handleScroll = (event: React.UIEvent<HTMLUListElement>): void => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollHeight - listboxNode.scrollTop ===
        listboxNode.clientHeight &&
      !loading &&
      hasNextPage
    ) {
      const nextCursor = cursors[Object.keys(cursors).length];
      fetchDocuments(query, nextCursor || null);
    }
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: Document | null,
  ): void => {
    setChanged();
    setSelected(newValue);
    setValue(newValue);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        justifyContent: 'center',
        justifyItems: 'center',
        textAlign: 'justify',
      }}
    >
      <Autocomplete
        id='reference-document-field'
        options={documents || []}
        loading={loading}
        sx={{ width: '100%' }}
        onInputChange={(_event, newInputValue) => setQuery(newInputValue)}
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setIsOpen(false);
          setDocuments(null);
        }}
        ListboxProps={{
          onScroll: handleScroll,
        }}
        value={selected || undefined}
        onChange={handleChange}
        disabled={edit}
        getOptionLabel={(option) =>
          option ? `${option.doc_id || ''} - ${option.name}` : ''
        }
        disableClearable={true}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`Select the Governing Document for the Manifest`}
            placeholder={
              !selected ? 'Click to load document list or start typing...' : ''
            }
            helperText={`The Governing Document is the source of the rules the Manifest will use to determine document compliance.`}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}

export default ManifestReferenceDocumentField;
